import { LuCircleAlert, LuTriangleAlert } from 'react-icons/lu'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import useChannel from '../models/useChannel'
import useNodeMetadata from '../models/useNodeMetadata'
import { isNodeOffline } from '../models/useNode'
import useUpgrades from '../models/useUpgrades'
import { effectiveChannel, nodeVersionMark, type VersionMark } from '../utilities/nodeVersion'

type Props = {
  node: NodeRecord | undefined
  network: NetworkRecord | undefined
}

// The upgrade mark for one node (app#361), from the stores it already has: the node's version
// (`node_metadata`), its pending upgrade record (`upgrades`) and its channel's floor. The rule
// itself is `utilities/nodeVersion.ts`.
const useNodeVersionMark = ({ node, network }: Props): VersionMark | null => {
  const allChannels = useChannel(state => state.allChannels)
  const version = useNodeMetadata(state => (node?.id ? state.getByNodeId(node.id)?.buildVersion : undefined))
  const upgrade = useUpgrades(state => (node?.id ? state.getByNodeId(node.id) : undefined))
  if (!node) return null
  return nodeVersionMark({
    version,
    channel: effectiveChannel(node.channelId, network?.channelId, allChannels),
    upgrade,
    offline: isNodeOffline(node),
    onlineAt: node.onlineAt,
    now: Date.now(),
  })
}

// The detail with its `commands` as code, so a reader can see what to type.
const Detail = ({ text }: { text: string }) => (
  <>
    {text.split('`').map((part, i) =>
      i % 2 ? (
        <Box key={i} component="code" sx={{ fontFamily: 'monospace' }}>
          {part}
        </Box>
      ) : (
        part
      )
    )}
  </>
)

// The mark beside the node's name in a list: an icon, with the explanation on hover.
export default function NodeVersionMark(props: Props) {
  const mark = useNodeVersionMark(props)
  if (!mark) return null
  const Icon = mark.kind === 'held' ? LuTriangleAlert : LuCircleAlert
  return (
    <Tooltip
      title={
        <Box>
          <Box sx={{ fontWeight: 500 }}>{mark.title}</Box>
          <Detail text={mark.detail} />
        </Box>
      }
    >
      <Box
        component="span"
        role="img"
        aria-label={mark.title}
        sx={{
          display: 'inline-flex',
          color: mark.kind === 'held' ? 'error.main' : 'warning.main',
        }}
      >
        <Icon size={16} />
      </Box>
    </Tooltip>
  )
}

// The same condition as a banner, for the node page.
export function NodeVersionBanner(props: Props) {
  const mark = useNodeVersionMark(props)
  if (!mark) return null
  return (
    <Alert severity={mark.kind === 'held' ? 'error' : 'warning'}>
      <AlertTitle>{mark.title}</AlertTitle>
      <Detail text={mark.detail} />
    </Alert>
  )
}
